import { Cross2Icon } from '@radix-ui/react-icons';
import { keepPreviousData } from '@tanstack/react-query';
import { Navigate, createFileRoute } from '@tanstack/react-router';
import { Loader2Icon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { z } from 'zod';

import { parseApiError } from '@/api/api-axios';
import { useGetEstateBranchesQuery } from '@/api/api-hooks';
import { RequireFeatureFlag } from '@/components/Auth/RequireFeatureFlag';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { Pagination } from '@/components/BaseTable/Pagination';
import { TableEmptyState } from '@/components/BaseTable/TableEmptyState';
import { BranchCard } from '@/components/Branches/BranchCard';
import { BranchGroupTabs } from '@/components/Branches/BranchGroupTabs';
import { BranchListSection } from '@/components/Branches/BranchListSection';
import { BranchListSkeleton } from '@/components/Branches/BranchListSkeleton';
import { BranchStatusFilter } from '@/components/Branches/BranchStatusFilter';
import { BranchWindowToggle } from '@/components/Branches/BranchWindowToggle';
import { CoverageBanner } from '@/components/Branches/CoverageBanner';
import { GroupPlaceholder } from '@/components/Branches/GroupPlaceholder';
import { CommonFilter } from '@/components/Filters/CommonFilter';
import { SearchFilter } from '@/components/Filters/SearchFilter';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { BRANCH_ESTATE_CAPABILITIES } from '@/constants/branchEstateCapabilities';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useBranchPaginationTable } from '@/hooks/useBranchPaginationTable';
import { useCodebaseCardSearch } from '@/hooks/useCodebaseCardSearch';
import { useDebouncedSearchValue } from '@/hooks/useDebouncedSearchValue';
import { useUser } from '@/hooks/useUser';
import { AppLink } from '@/types/navigation';
import { toEstateBranchesParams } from '@/utils/estate';
import { pluralize } from '@/utils/pluralize';
import { branchesRouteApi } from '@/utils/route';
import { searchParam } from '@/utils/router';

import type { DashboardStaticData } from './route';

const staticData: DashboardStaticData = {
  navbarTitle: 'Branch activity',
};

const branchesSearchSchema = z.object({
  window: z.enum(['last_7d', 'last_30d', 'last_90d', 'all']).default('last_30d').catch('last_30d'),
  // 'merged' is intentionally omitted (merged lifecycle is gated off in v1, BRANCH_ESTATE_CAPABILITIES
  // .mergedLifecycle) so a stale/bookmarked ?status=merged URL is coerced away by the fallback.
  status: z
    .array(z.enum(['active', 'stale']))
    .default([])
    .catch([]),
  codebase: z.array(z.string()).default([]).catch([]),
  search: searchParam().optional(),
  group: z.enum(['branch', 'codebase', 'status', 'team']).default('branch').catch('branch'),
  sortBy: z.enum(['last_commit_date', 'name', 'unique_commits', 'current_sloc']).catch('last_commit_date').optional(),
  sortDirection: z.enum(['ASC', 'DESC']).catch('DESC').optional(),
  pageIndex: z.number().default(0).catch(0),
  pageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  branch: searchParam().optional(),
  // Branch names are unique only within a codebase, not across the estate — `branchCodebase`
  // (the codebase_id) disambiguates which codebase's branch the detail sheet opens.
  branchCodebase: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/_dashboard/branches')({
  component: GatedBranchesPage,
  staticData,
  validateSearch: branchesSearchSchema,
});

// Gate the org-level branches surface behind the BRANCHES_UI flag. When the flag is
// loaded-and-off, redirect to the org sources page; while loading, show the branch-list
// skeleton (avoids flashing a redirect before /me resolves). See RequireFeatureFlag.
function GatedBranchesPage() {
  const { routeOrganizationName } = useUser();
  return (
    <RequireFeatureFlag
      flag={FEATURE_FLAGS.BRANCHES_UI}
      loading={<BranchListSkeleton />}
      redirect={
        <Navigate to="/$organizationName/sources" replace params={{ organizationName: routeOrganizationName }} />
      }
    >
      <BranchesPage />
    </RequireFeatureFlag>
  );
}

function BranchesPage() {
  const { organizationDisplayName, routeOrganizationName } = useUser();
  const search = branchesRouteApi.useSearch();
  const navigate = branchesRouteApi.useNavigate();

  const { searchValue, setSearchValue } = useDebouncedSearchValue<typeof search>({
    searchQuery: search.search,
    navigate,
    searchParamName: 'search',
    resetPageIndexParamName: 'pageIndex',
  });

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Branch activity`;
  }, [organizationDisplayName]);

  // All filtering / sorting / pagination is server-side: the URL search params are mapped to
  // request params and sent to the estate endpoint. The returned `items` are rendered as-is —
  // no client-side `.filter()` / `.sort()`. `window` is wired through but is a no-op against the
  // current mock backend.
  const params = useMemo(() => toEstateBranchesParams(search), [search]);
  // `keepPreviousData` keeps the last successful page on screen while a new param set is fetching,
  // so changing filters/range doesn't blank the whole page to skeletons. The in-place `isFetching`
  // indicator covers the refetch; only the very first load (no data yet) shows the skeleton.
  const {
    data: estateBranchesData,
    error,
    isLoading,
    isError,
    isFetching,
  } = useGetEstateBranchesQuery(params, {
    placeholderData: keepPreviousData,
  });

  // Codebase filter options come from the same grant-filtered source that powers /sources
  // (`/codebase_card/`). Server-side search via `useCodebaseCardSearch` avoids capping the
  // dropdown at the first page of results when an org has more than 100 codebases.
  // Selections are written to the URL as `codebase[]` and forwarded to the server as `codebase_id[]`.
  const selectedCodebaseCount = (search.codebase ?? []).length;
  const {
    options: codebaseOptions,
    onSearchChange: onCodebaseSearchChange,
    isLoadingOptions: isCodebaseOptionsLoading,
    fetchNextPage: fetchNextCodebasePage,
    hasNextPage: hasMoreCodebases,
    isFetchingNextPage: isFetchingMoreCodebases,
  } = useCodebaseCardSearch(true, selectedCodebaseCount === 1 ? search.codebase?.[0] : undefined);

  const branches = useMemo(() => estateBranchesData?.items ?? [], [estateBranchesData]);
  const summary = estateBranchesData?.summary;
  const totalCount = estateBranchesData?.total_count ?? 0;

  const paginationTable = useBranchPaginationTable({
    branches,
    totalCount,
    pagination: { pageIndex: search.pageIndex, pageSize: search.pageSize },
    onPaginationChange: ({ pageIndex, pageSize }) => {
      navigate({ search: (prev) => ({ ...prev, pageIndex, pageSize }) });
    },
  });

  // Only "By branch" grouping is implemented; "By codebase"/"By status" are placeholders, so the
  // whole tab row is hidden and the list stays pinned to 'branch' (BRANCH_ESTATE_CAPABILITIES.grouping).
  const showGroupingTabs = BRANCH_ESTATE_CAPABILITIES.grouping;
  const activeGroup = showGroupingTabs ? search.group : 'branch';

  const setSearchParams = (updates: Partial<typeof search>) => {
    navigate({ search: (prev) => ({ ...prev, pageIndex: 0, ...updates }) });
  };

  const toggleStatus = (value: 'active' | 'merged' | 'stale') => {
    // 'merged' is gated out of the filter UI and coerced out of the URL, so only active/stale reach
    // here; the early return also narrows the type to the route's status enum.
    if (value === 'merged') return;
    const next = (search.status ?? []).includes(value)
      ? (search.status ?? []).filter((s) => s !== value)
      : [...(search.status ?? []), value];
    setSearchParams({ status: next });
  };

  const codebaseLabel =
    selectedCodebaseCount === 0
      ? 'All codebases'
      : selectedCodebaseCount === 1
        ? (codebaseOptions.find((cb) => cb.value === search.codebase?.[0])?.label ?? '1 codebase')
        : `${selectedCodebaseCount} codebases`;

  const numberOfActiveFilters = useMemo(() => {
    const searchCount = (search.search?.length ?? 0) > 0 ? 1 : 0;
    const statusCount = (search.status ?? []).length > 0 ? 1 : 0;
    const codebaseCount = (search.codebase ?? []).length > 0 ? 1 : 0;
    return searchCount + statusCount + codebaseCount;
  }, [search.search, search.status, search.codebase]);

  const resetAllFilters = useCallback(() => {
    setSearchValue('');
    onCodebaseSearchChange('');
    navigate({ search: (prev) => ({ ...prev, pageIndex: 0, search: undefined, status: [], codebase: [] }) });
  }, [navigate, onCodebaseSearchChange, setSearchValue]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <BranchStatusFilter
            status={search.status ?? []}
            summary={summary}
            onClearStatus={() => setSearchParams({ status: [] })}
            onToggle={toggleStatus}
          />

          <BranchWindowToggle value={search.window} onValueChange={(window) => setSearchParams({ window })} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <SearchFilter
            name="estate-branch-search"
            placeholder="Search across all branches…"
            value={searchValue ?? ''}
            onChange={setSearchValue}
            className="w-72"
          />
          <div className="flex shrink-0 items-center gap-4">
            {selectedCodebaseCount > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  onCodebaseSearchChange('');
                  setSearchParams({ codebase: [] });
                }}
                className="bg-muted hover:bg-muted/80 h-8 shrink-0 px-2 text-xs lg:px-3"
              >
                Reset
                <Cross2Icon className="ml-2 h-4 w-4" />
              </Button>
            )}
            <CommonFilter
              multiSelect
              title="Codebase"
              placeholder="Search codebases…"
              emptyMessage="No codebases available"
              showLabel
              labelValue={codebaseLabel}
              value={search.codebase ?? []}
              onChange={(codebase) => setSearchParams({ codebase })}
              options={codebaseOptions}
              onSearchChange={onCodebaseSearchChange}
              isLoadingOptions={isCodebaseOptionsLoading}
              onScrollEnd={fetchNextCodebasePage}
              hasMoreToLoad={hasMoreCodebases}
              isLoadingMore={isFetchingMoreCodebases}
              width="w-[260px]"
            />
          </div>
        </div>
      </div>

      {/* The CoverageBanner + tab row is persistent chrome — it always renders (fed by the latest
          or previous `summary`) and is never swapped out by the loading/error state. Only the
          branch-list region below reacts to loading / error / empty. */}
      <BranchListSection>
        <CoverageBanner
          bare
          activeCount={summary?.active ?? 0}
          mergedCount={summary?.merged ?? 0}
          staleCount={summary?.stale ?? 0}
          generatedCount={summary?.with_context ?? 0}
          totalCount={summary?.total ?? 0}
          commitCount={summary?.total_commits}
          isLoading={isLoading}
          tabsSlot={
            showGroupingTabs ? (
              <BranchGroupTabs
                activeGroup={activeGroup}
                onGroupChange={(group) => setSearchParams({ group })}
                rightSlot={
                  isFetching && !isLoading ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2Icon className="h-3 w-3 animate-spin" />
                      Updating…
                    </span>
                  ) : activeGroup === 'branch' ? (
                    'Click any branch for detail'
                  ) : undefined
                }
              />
            ) : undefined
          }
        />

        {isError && !estateBranchesData ? (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load branch activity</AlertTitle>
              <AlertDescription>
                {parseApiError(error, 'Something went wrong fetching branches across your estate. Try again.').message}
              </AlertDescription>
            </Alert>
          </div>
        ) : isLoading ? (
          <BranchListSkeleton />
        ) : activeGroup === 'branch' ? (
          branches.length === 0 ? (
            <TableEmptyState
              heading="No branches found across your codebases."
              subHeading={numberOfActiveFilters > 0 ? 'Try a different search.' : undefined}
              actionText={
                numberOfActiveFilters > 0
                  ? `Clear ${numberOfActiveFilters} ${pluralize(numberOfActiveFilters, 'filter')}`
                  : undefined
              }
              actionIcon={numberOfActiveFilters > 0 ? <XIcon className="ml-2 h-4 w-4" /> : undefined}
              actionOnClick={numberOfActiveFilters > 0 ? resetAllFilters : undefined}
              actionButtonVariant="outline"
              className="flex flex-col items-center justify-center py-16"
            />
          ) : (
            <>
              <div className="flex flex-col gap-2 p-4">
                {branches.map((branch) => {
                  const link: AppLink = {
                    to: '/$organizationName/branches',
                    params: { organizationName: routeOrganizationName },
                    search: (prev) => ({
                      ...prev,
                      pageIndex: 0,
                      branch: branch.name,
                      branchCodebase: branch.codebase_id,
                    }),
                  };

                  return (
                    <BranchCard
                      key={`${branch.codebase_id}/${branch.name}`}
                      branch={branch}
                      link={link}
                      showCodebaseName
                    />
                  );
                })}
              </div>
              <div className="px-4 pb-4">
                <Pagination table={paginationTable} total={totalCount} />
              </div>
            </>
          )
        ) : (
          <div className="p-4">
            <GroupPlaceholder group={activeGroup} />
          </div>
        )}
      </BranchListSection>
    </div>
  );
}
